import {
  forwardRef,
  type InputHTMLAttributes,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { cn } from "@/lib/utils";

// Rounded input with soft focus ring. Pure white background. Friendly.
const baseInput =
  "w-full rounded-md bg-surface px-3.5 py-2.5 text-[0.95rem] text-ink " +
  "border border-rule transition-all " +
  "focus:outline-none focus:border-primary focus:shadow-ring " +
  "placeholder:text-ink-faint " +
  "disabled:opacity-50 disabled:cursor-not-allowed";

export const Input = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement>
>(({ className, ...props }, ref) => {
  return <input ref={ref} className={cn(baseInput, className)} {...props} />;
});
Input.displayName = "Input";

export const Select = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement>
>(({ className, children, ...props }, ref) => {
  return (
    <select
      ref={ref}
      className={cn(baseInput, "cursor-pointer appearance-auto", className)}
      {...props}
    >
      {children}
    </select>
  );
});
Select.displayName = "Select";

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ className, rows = 2, ...props }, ref) => {
  return (
    <textarea
      ref={ref}
      rows={rows}
      className={cn(baseInput, "resize-y min-h-[4.5rem]", className)}
      {...props}
    />
  );
});
Textarea.displayName = "Textarea";

export function Field({
  label,
  helper,
  required,
  error,
  children,
}: {
  label: string;
  helper?: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="mb-1.5 flex items-baseline justify-between gap-3">
        <span className="text-[0.86rem] font-medium text-ink-soft">
          {label}
          {required && <span className="ml-1 text-primary">*</span>}
        </span>
        {helper && !error && (
          <span className="text-[0.78rem] text-ink-muted">{helper}</span>
        )}
      </div>
      {children}
      {error && (
        <div className="mt-1.5 text-[0.8rem] text-danger">{error}</div>
      )}
    </label>
  );
}
